import { isUUID } from 'class-validator';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/** Every tab id the content editor and viewer can show in the URL hash. */
export const TAB_IDS = [
  'attachments',
  'authors',
  'code',
  'contents',
  'history',
  'metadata',
  'update-requests',
] as const;

/** A tab the content editor or viewer can show. */
export type TabId = (typeof TAB_IDS)[number];

/** Returns true when the value is one of {@link TAB_IDS}. */
export function isTabId(value: string): value is TabId {
  return TAB_IDS.some((tabId) => tabId === value);
}

/** A deep link to one update request, e.g. `#feedback-<uuid>`. */
interface FeedbackLink {
  readonly kind: 'feedback';
  readonly feedbackId: string;
}

/** A link to a tab, optionally with a section on it, e.g. `#contents?section=overview`. */
interface TabLink {
  readonly kind: 'tab';
  readonly tabId: TabId;
  readonly sectionId: string | undefined;
}

/** A hash that points nowhere, e.g. an empty hash or `#unknown?section=a`. */
interface NoLink {
  readonly kind: 'none';
}

/**
 * Where a URL hash points to. It takes one of three forms:
 *
 * 1. Feedback link: `#feedback-<uuid>` → `{ kind: 'feedback', feedbackId: '<uuid>' }`.
 *    A non-UUID suffix such as `#feedback-loops` is a short section alias instead.
 * 2. Tab link:
 *    - `#contents?section=overview` → `{ kind: 'tab', tabId: 'contents', sectionId: 'overview' }`
 *    - `#history` → `{ kind: 'tab', tabId: 'history', sectionId: undefined }`
 *    - `#overview` (short alias for any value that isn't a tab id) →
 *      `{ kind: 'tab', tabId: 'contents', sectionId: 'overview' }`
 * 3. No link: ``, `#`, `#?section=a`, `#unknown?section=a` → `{ kind: 'none' }`
 */
type UrlHashLink = FeedbackLink | TabLink | NoLink;

/**
 * Reads where a URL hash points to.
 *
 * @example
 * parseUrlHash('#feedback-<uuid>');            // { kind: 'feedback', feedbackId: '<uuid>' }
 * parseUrlHash('#feedback-loops');             // { kind: 'tab', tabId: 'contents', sectionId: 'feedback-loops' }
 * parseUrlHash('#contents?section=overview');  // { kind: 'tab', tabId: 'contents', sectionId: 'overview' }
 * parseUrlHash('#history');                    // { kind: 'tab', tabId: 'history', sectionId: undefined }
 * parseUrlHash('#overview');                   // { kind: 'tab', tabId: 'contents', sectionId: 'overview' }
 * parseUrlHash('#unknown?section=a');          // { kind: 'none' }
 * parseUrlHash('');                            // { kind: 'none' }
 */
function parseUrlHash(hash: string): UrlHashLink {
  const hashBody = hash.startsWith('#') ? hash.slice(1) : hash;
  if (hashBody.length === 0) return { kind: 'none' };

  // Deep links to one update request, e.g. from notification emails. Requiring a UUID keeps
  // short section aliases like "#feedback-loops" from being read as feedback links.
  const feedbackId = /^feedback-(.+)$/.exec(hashBody)?.[1];
  if (feedbackId && isUUID(feedbackId)) return { kind: 'feedback', feedbackId };

  // Full form "<tab>?section=<id>". Only a known tab makes it a valid location.
  const queryIndex = hashBody.indexOf('?');
  if (queryIndex >= 0) {
    const tabText = hashBody.slice(0, queryIndex);
    if (!isTabId(tabText)) return { kind: 'none' };

    const section = new URLSearchParams(hashBody.slice(queryIndex + 1)).get('section');
    return { kind: 'tab', tabId: tabText, sectionId: section || undefined };
  }

  // A bare tab id, e.g. "#history".
  if (isTabId(hashBody)) return { kind: 'tab', tabId: hashBody, sectionId: undefined };

  // Short alias "#<section-id>", used by in-content links written for the legacy UI.
  return { kind: 'tab', tabId: 'contents', sectionId: hashBody };
}

/** Returns the current tab from the URL hash, or undefined when the hash points to no tab. */
export function useCurrentTabId(): TabId | undefined {
  const { hash } = useLocation();
  return useMemo(() => {
    const link = parseUrlHash(hash);
    switch (link.kind) {
      case 'feedback':
        return 'update-requests';
      case 'tab':
        return link.tabId;
      case 'none':
        return undefined;
    }
  }, [hash]);
}

/** Returns the current section ID from a tab link in the URL hash, regardless of the active tab. */
export function useCurrentSectionId(): string | undefined {
  const { hash } = useLocation();
  return useMemo(() => {
    const link = parseUrlHash(hash);
    return link.kind === 'tab' ? link.sectionId : undefined;
  }, [hash]);
}

/** Returns the feedback ID when the URL hash is a `#feedback-<uuid>` deep link. */
export function useCurrentFeedbackId(): string | undefined {
  const { hash } = useLocation();
  return useMemo(() => {
    const link = parseUrlHash(hash);
    return link.kind === 'feedback' ? link.feedbackId : undefined;
  }, [hash]);
}

/**
 * Returns a function that changes tabs while keeping URL search params and the
 * current section. Leaving a `#feedback-<uuid>` link drops the feedback id.
 */
export function useNavigateToTab(): (tabId: TabId) => void {
  const history = useHistory();
  const { hash, search } = useLocation();

  return useCallback(
    (tabId: TabId) => {
      const link = parseUrlHash(hash);

      // If the section ID parameter is available, preserve it
      const sectionId = link.kind === 'tab' ? link.sectionId : undefined;

      // Encode the section ID because parseUrlHash decodes it, e.g. "a&b" must stay one value
      const newHash = sectionId ? `${tabId}?section=${encodeURIComponent(sectionId)}` : tabId;
      history.replace({ search, hash: newHash });
    },
    [hash, search, history],
  );
}

/** Returns a function that opens a section on the Contents tab. */
export function useNavigateToSection(): (sectionId: string) => void {
  const history = useHistory();
  const { search } = useLocation();
  return useCallback(
    (sectionId: string) => {
      history.replace({ search, hash: `contents?section=${encodeURIComponent(sectionId)}` });
    },
    [history, search],
  );
}
